import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  alpha,
  Skeleton,
  useMediaQuery,
  Badge,
  Stack,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Cake as CakeIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  PersonAdd as PersonAddIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCardOff as CreditCardOffIcon,
  Add as AddIcon,
  ShowChart as ShowChartIcon,
  Router as RouterIcon,
  Wifi as WifiIcon,
  Block as BlockIcon,
  Engineering as EngineeringIcon,
  WhatsApp as WhatsAppIcon,
  Warning as WarningIcon,
  FiberManualRecord as FiberIcon,
  Speed as SpeedIcon,
  Handyman as HandymanIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Line,
  Bar,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  LineChart,
  BarChart,
  Area,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import logService from '../../services/logService';
import { Log } from '../../types';
import { clientService } from '../../services/clientService';
import { Client } from '../../types';
import { dashboardService } from '../../services/dashboardService';
import despesasService from '../../services/despesasService';
import { contractService } from '../../services/contractService';
import { atendimentoService } from '../../services/atendimentoService';
import { oltService } from '../../services/oltService';
import {
  dashboardPageSx,
  getChartThemeColors,
  getRechartsStyles,
  dashboardChartCardSx,
} from '../../styles/theme';
import fundoHeroDashboard from '../../assets/fundo_hero_dashboard.png';
import fundoHeroCopa from '../../assets/fundo_hero_copa.png';
import fundoHeroRosa from '../../assets/fundo_hero_rosa.png';
import fundoHeroAzulEscuro from '../../assets/fundo_hero_azul_escuro.png';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtInt = (n?: number) => (n ?? 0).toLocaleString('pt-BR');

const fmtCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const fmtCurrencyShort = (value?: number) => {
  const v = value ?? 0;
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(0)}K`;
  return fmtCurrency(v);
};

const fmtMes = (mes: string) => {
  if (!mes) return '';
  const [ano, m] = mes.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[parseInt(m, 10) - 1]}/${(ano ?? '').slice(2)}`;
};

const QUERY_OPTS = {
  staleTime: 60_000,
  refetchOnWindowFocus: true,
  refetchInterval: 60_000,
} as const;

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const SecaoHeader: React.FC<{
  titulo: string;
  subtitulo?: string;
  action?: React.ReactNode;
}> = ({ titulo, subtitulo, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {titulo}
      </Typography>
      {subtitulo && (
        <Typography variant="caption" color="text.secondary">
          {subtitulo}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

interface KpiCardProps {
  titulo: string;
  valor: React.ReactNode;
  subtitulo?: string;
  icone: React.ReactElement;
  cor: string;
  loading?: boolean;
  onClick?: () => void;
  badge?: React.ReactNode;
  progress?: number;
}

const KpiCard: React.FC<KpiCardProps> = ({
  titulo,
  valor,
  subtitulo,
  icone,
  cor,
  loading,
  onClick,
  badge,
  progress,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={2}
      onClick={onClick}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.22s ease',
        '&:hover': onClick
          ? { transform: 'translateY(-3px)', boxShadow: theme.shadows[8], borderColor: alpha(cor, 0.4) }
          : {},
        border: '1px solid transparent',
      }}
    >
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${cor} 0%, ${alpha(cor, 0.4)} 100%)` }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}
          >
            {titulo}
          </Typography>
          <Avatar sx={{ bgcolor: alpha(cor, 0.12), color: cor, width: 36, height: 36 }}>
            {React.cloneElement(icone, { sx: { fontSize: 18 } })}
          </Avatar>
        </Box>
        {loading ? (
          <Skeleton width="60%" height={36} />
        ) : (
          <Typography variant="h5" fontWeight={800} lineHeight={1.15}>
            {valor}
          </Typography>
        )}
        {subtitulo && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {loading ? <Skeleton width="80%" /> : subtitulo}
          </Typography>
        )}
        {badge}
        {progress !== undefined && !loading && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            sx={{
              mt: 1.25,
              height: 5,
              borderRadius: 3,
              bgcolor: alpha(cor, 0.12),
              '& .MuiLinearProgress-bar': { bgcolor: cor, borderRadius: 3 },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

const ChartCard: React.FC<{
  titulo: string;
  subtitulo?: string;
  onRefresh?: () => void;
  loading?: boolean;
  height?: number;
  children: React.ReactNode;
}> = ({ titulo, subtitulo, onRefresh, loading, height = 300, children }) => {
  const theme = useTheme();
  return (
    <Card elevation={2} sx={dashboardChartCardSx(theme)}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="body1" fontWeight={700}>
              {titulo}
            </Typography>
            {subtitulo && (
              <Typography variant="caption" color="text.secondary">
                {subtitulo}
              </Typography>
            )}
          </Box>
          {onRefresh && (
            <IconButton size="small" onClick={onRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ height, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const HeroSection: React.FC<{
  user: { nome?: string } | null;
  stats: {
    activeClients: number;
    monthlyRevenue: number;
    pendingTickets: number;
    crescimentoMensal: number;
    contratosAtivos: number;
    oltsOnline: number;
    oltsTotal: number;
  } | null;
  loading?: boolean;
}> = ({ user, stats, loading }) => {
  const theme = useTheme();
  const { themeId } = useAppTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartColors = getChartThemeColors(theme);

  const heroBackground =
    themeId === 'copa'
      ? fundoHeroCopa
      : themeId === 'rosa'
        ? fundoHeroRosa
        : themeId === 'usa'
          ? fundoHeroAzulEscuro
          : fundoHeroDashboard;

  const crescimento = stats?.crescimentoMensal ?? 0;
  const firstName = user?.nome?.split(' ')[0] ?? 'Usuário';
  const growthColor =
    crescimento > 0 ? theme.palette.success.main : crescimento < 0 ? theme.palette.error.main : theme.palette.warning.main;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const mesAtual = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const statItems = [
    { label: 'Assinantes ativos', value: fmtInt(stats?.activeClients), icon: <PeopleIcon sx={{ fontSize: 16 }} /> },
    { label: 'MRR (receita mês)', value: fmtCurrencyShort(stats?.monthlyRevenue), icon: <AttachMoneyIcon sx={{ fontSize: 16 }} /> },
    { label: 'Contratos ativos', value: fmtInt(stats?.contratosAtivos), icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
    { label: 'Chamados abertos', value: fmtInt(stats?.pendingTickets), icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
  ];

  const oltPct =
    stats && stats.oltsTotal > 0 ? Math.round((stats.oltsOnline / stats.oltsTotal) * 100) : 0;

  return (
    <Fade in timeout={600}>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          color: isDark ? '#e2e8f0' : '#ffffff',
          border: isDark ? `1px solid ${alpha('#3b82f6', 0.22)}` : 'none',
          background: isDark
            ? `linear-gradient(145deg, #0a101f 0%, #0d1526 35%, #152347 70%, ${alpha(theme.palette.primary.main, 0.35)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: isDark ? `0 24px 48px rgba(0,0,0,0.4)` : theme.shadows[6],
        }}
      >
        {!isMobile && (
          <Box
            component="img"
            src={heroBackground}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              height: '78%',
              maxWidth: { md: '46%', lg: '42%' },
              objectFit: 'contain',
              objectPosition: 'right bottom',
              pointerEvents: 'none',
              zIndex: 0,
              opacity: isDark ? 0.85 : 1,
            }}
          />
        )}
        <CardContent sx={{ p: isMobile ? 2 : 3, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <FiberIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: '0.06em' }}>
                  CENTRAL DE COMANDO · PROVEDOR
                </Typography>
              </Stack>
              <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.02em' }}>
                {getGreeting()}, {firstName}! 👋
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.88, mb: 2 }}>
                Visão executiva de {mesAtual} — base, financeiro, operação e rede FTTH
              </Typography>

              {loading ? (
                <Skeleton width="50%" height={24} sx={{ bgcolor: alpha('#fff', 0.1) }} />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                  {crescimento > 0 ? (
                    <TrendingUpIcon sx={{ color: growthColor, fontSize: 20 }} />
                  ) : crescimento < 0 ? (
                    <TrendingDownIcon sx={{ color: growthColor, fontSize: 20 }} />
                  ) : (
                    <TrendingFlatIcon sx={{ color: growthColor, fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ opacity: 0.92 }}>
                    {crescimento < 0
                      ? `Sua empresa recuou ${Math.abs(crescimento)}% este mês`
                      : `Sua empresa cresceu ${crescimento}% este mês`}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${crescimento > 0 ? '+' : ''}${crescimento}%`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(growthColor, isDark ? 0.22 : 0.25),
                      color: isDark ? '#e2e8f0' : '#fff',
                      border: `1px solid ${alpha(growthColor, 0.45)}`,
                    }}
                  />
                </Stack>
              )}

              <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} flexWrap="wrap">
                {statItems.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      flex: isMobile ? '1 1 100%' : '1 1 140px',
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: alpha('#fff', isDark ? 0.06 : 0.14),
                      border: `1px solid ${alpha('#fff', isDark ? 0.1 : 0.2)}`,
                    }}
                  >
                    {loading ? (
                      <>
                        <Skeleton width="60%" height={12} sx={{ bgcolor: alpha('#fff', 0.08) }} />
                        <Skeleton width="50%" height={24} sx={{ bgcolor: alpha('#fff', 0.1) }} />
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, opacity: 0.85 }}>
                          {stat.icon}
                          <Typography variant="caption" fontWeight={600}>
                            {stat.label}
                          </Typography>
                        </Box>
                        <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={800}>
                          {stat.value}
                        </Typography>
                      </>
                    )}
                  </Box>
                ))}
              </Stack>
            </Grid>

            {!isMobile && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ position: 'relative', width: 140, height: 140 }}>
                    {loading ? (
                      <Skeleton variant="circular" width={140} height={140} sx={{ bgcolor: alpha('#fff', 0.08) }} />
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadialBarChart
                            cx="50%"
                            cy="50%"
                            innerRadius="70%"
                            outerRadius="98%"
                            barSize={9}
                            data={[{ value: oltPct || Math.min(Math.abs(crescimento), 100) }]}
                            startAngle={90}
                            endAngle={-270}
                          >
                            <RadialBar
                              dataKey="value"
                              cornerRadius={6}
                              fill={stats?.oltsTotal ? theme.palette.info.main : growthColor}
                              background={{ fill: chartColors.radialRemainder }}
                            />
                          </RadialBarChart>
                        </ResponsiveContainer>
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h5" fontWeight={800} lineHeight={1}>
                            {stats?.oltsTotal ? `${oltPct}%` : `${crescimento}%`}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            {stats?.oltsTotal ? 'OLTs online' : 'crescimento'}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                  {stats?.oltsTotal ? (
                    <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.75 }}>
                      {stats.oltsOnline}/{stats.oltsTotal} OLTs operacionais
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );
};

interface Aniversariante {
  id: number;
  nome: string;
  data: string;
  dia: number;
  mes: number;
}

// ─── Dashboard Principal ───────────────────────────────────────────────────────

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColors = getChartThemeColors(theme);
  const rechartsStyles = getRechartsStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const { data: dashStats, isLoading: loadingDash, refetch: refetchDash } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getStats(),
    ...QUERY_OPTS,
  });

  const { data: contractStatsResp, isLoading: loadingContracts } = useQuery({
    queryKey: ['contract-stats'],
    queryFn: () => contractService.getStats(),
    ...QUERY_OPTS,
  });

  const { data: expenseStats, isLoading: loadingExpenses } = useQuery({
    queryKey: ['expense-stats', currentMonth, currentYear],
    queryFn: async () => {
      const res = await despesasService.getStats({ mes: currentMonth, ano: currentYear });
      return res.success ? res.data : null;
    },
    ...QUERY_OPTS,
  });

  const { data: performanceData, isLoading: loadingPerformance, refetch: refetchPerformance } = useQuery({
    queryKey: ['performance-data'],
    queryFn: async () => {
      const response = await dashboardService.getPerformanceChart();
      return Array.isArray(response) ? response : [];
    },
    ...QUERY_OPTS,
  });

  const { data: regChart, isLoading: loadingReg, refetch: refetchReg } = useQuery({
    queryKey: ['registrations-cancellations'],
    queryFn: async () => {
      const response = await dashboardService.getRegistrationsCancellationsChart();
      return Array.isArray(response) ? response : [];
    },
    ...QUERY_OPTS,
  });

  const { data: overdueChart, isLoading: loadingOverdue, refetch: refetchOverdue } = useQuery({
    queryKey: ['overdue-clients-chart'],
    queryFn: async () => {
      const response = await dashboardService.getOverdueClientsChart();
      return Array.isArray(response) ? response : [];
    },
    ...QUERY_OPTS,
  });

  const { data: clientStatusChart, isLoading: loadingClientStatus } = useQuery({
    queryKey: ['client-status-chart'],
    queryFn: () => dashboardService.getClientStatusChart(),
    ...QUERY_OPTS,
  });

  const { data: planosChart, isLoading: loadingPlanos } = useQuery({
    queryKey: ['dashboard-contrato-servicos-por-plano'],
    queryFn: () => dashboardService.getContratoServicosPorPlano(),
    ...QUERY_OPTS,
  });

  const { data: oltStats } = useQuery({
    queryKey: ['olt-stats-dashboard'],
    queryFn: () => oltService.getStats(),
    ...QUERY_OPTS,
    retry: false,
  });

  const { data: atendimentoData } = useQuery({
    queryKey: ['atendimento-dashboard-admin'],
    queryFn: () => atendimentoService.getDashboard(),
    ...QUERY_OPTS,
    retry: false,
  });

  const { data: logs, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['dashboard-logs'],
    queryFn: async () => {
      const response = await logService.getLogs({ limit: 8, page: 1 });
      return response.success && response.data ? response.data : [];
    },
    ...QUERY_OPTS,
  });

  const { data: aniversariantes } = useQuery({
    queryKey: ['aniversariantes'],
    queryFn: async (): Promise<Aniversariante[]> => {
      try {
        const response = await clientService.getAniversariantesMes();
        if (!response.success || !response.data) return [];
        return (response.data as Client[])
          .map((c) => {
            const data = new Date(String(c.data_nascimento));
            data.setMinutes(data.getMinutes() + data.getTimezoneOffset());
            return {
              id: c.id,
              nome: c.nome,
              data: data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
              dia: data.getDate(),
              mes: data.getMonth() + 1,
            };
          })
          .sort((a, b) => a.dia - b.dia);
      } catch {
        return [];
      }
    },
    ...QUERY_OPTS,
  });

  const { data: novosClientes } = useQuery({
    queryKey: ['novos-clientes'],
    queryFn: async () => {
      try {
        const response = await clientService.getNovosClientesMes();
        if (!response.success || !response.data) return [];
        return (response.data as Client[]).map((c) => ({
          id: c.id,
          nome: c.nome,
          cidade: c.cidade,
          estado: c.estado,
          status: c.status,
          data_cadastro: new Date(String(c.createdAt)).toLocaleDateString('pt-BR'),
        }));
      } catch {
        return [];
      }
    },
    ...QUERY_OPTS,
  });

  const contractStats = (contractStatsResp as { data?: typeof contractStatsResp } | undefined)?.data ?? contractStatsResp;
  const contratosAtivos = (contractStats as { ativos?: number })?.ativos ?? 0;
  const contratosBloqueados = (contractStats as { bloqueados?: number })?.bloqueados ?? 0;
  const aguardandoInstalacao = (contractStats as { aguardando_instalacao?: number })?.aguardando_instalacao ?? 0;
  const aguardandoAssinatura = (contractStats as { aguardando_assinatura?: number })?.aguardando_assinatura ?? 0;

  const oltsOnline = oltStats?.summary?.online ?? 0;
  const oltsTotal = oltStats?.summary?.total ?? 0;
  const oltsOffline = oltStats?.summary?.offline ?? 0;
  const onusAtivas = oltStats?.onus?.current ?? 0;

  const monthlyRevenue = dashStats?.monthlyRevenue ?? 0;
  const monthlyExpenses = expenseStats?.total?.value ?? 0;
  const lucroMes = monthlyRevenue - monthlyExpenses;
  const margemLucro = monthlyRevenue > 0 ? (lucroMes / monthlyRevenue) * 100 : 0;
  const taxaInadimplencia =
    (dashStats?.activeClients ?? 0) > 0
      ? ((dashStats?.defaulters ?? 0) / (dashStats?.activeClients ?? 1)) * 100
      : 0;

  const regData = useMemo(() => {
    if (!Array.isArray(regChart)) return [];
    return regChart.slice(-12).map((r: { month: string; registrations: number; cancellations: number }) => ({
      mes: fmtMes(r.month),
      cadastros: r.registrations,
      cancelamentos: r.cancellations,
      saldo: r.registrations - r.cancellations,
    }));
  }, [regChart]);

  const mesAtualReg = regData[regData.length - 1];
  const churnMes =
    contratosAtivos > 0 && mesAtualReg
      ? ((mesAtualReg.cancelamentos / contratosAtivos) * 100).toFixed(1)
      : '0';

  const performanceChartData = useMemo(() => {
    if (!Array.isArray(performanceData)) return [];
    return performanceData.map((item) => ({
      ...item,
      mes: fmtMes(item.month),
      lucro: Number(item.profit ?? item.revenue - item.expenses),
    }));
  }, [performanceData]);

  const clientePieData = useMemo(() => {
    if (!Array.isArray(clientStatusChart)) return [];
    const corMap: Record<string, string> = {
      Ativo: theme.palette.success.main,
      Inativo: theme.palette.grey[500],
      Suspenso: theme.palette.warning.main,
      Inadimplente: theme.palette.error.main,
    };
    return clientStatusChart
      .filter((c) => c.count > 0)
      .map((c) => ({
        name: c.status,
        value: c.count,
        color: corMap[c.status] ?? theme.palette.primary.main,
      }));
  }, [clientStatusChart, theme]);

  const contratoPieData = useMemo(
    () =>
      [
        { name: 'Ativos', value: contratosAtivos, color: theme.palette.success.main },
        { name: 'Bloqueados', value: contratosBloqueados, color: theme.palette.warning.main },
        { name: 'Ag. Instalação', value: aguardandoInstalacao, color: theme.palette.info.main },
        { name: 'Ag. Assinatura', value: aguardandoAssinatura, color: theme.palette.grey[500] },
      ].filter((d) => d.value > 0),
    [contratosAtivos, contratosBloqueados, aguardandoInstalacao, aguardandoAssinatura, theme]
  );

  const overdueData = useMemo(() => {
    if (!Array.isArray(overdueChart)) return [];
    return overdueChart.slice(-12).map((r: { month: string; clientes: number }) => ({
      mes: fmtMes(r.month),
      clientes: r.clientes,
    }));
  }, [overdueChart]);

  const planosBarData = useMemo(() => {
    if (!Array.isArray(planosChart)) return [];
    return planosChart.slice(0, 10).map((row) => ({
      nome: row.nome?.trim() || 'Sem plano',
      quantidade: row.quantidade,
    }));
  }, [planosChart]);

  const alertasCriticos = useMemo(() => {
    const items: { msg: string; severity: 'error' | 'warning' | 'info'; path: string }[] = [];
    if ((dashStats?.suspendedClients ?? 0) > 0) {
      items.push({
        msg: `${dashStats!.suspendedClients} contrato(s) suspenso(s) — serviço interrompido`,
        severity: 'error',
        path: '/contracts?status=suspenso',
      });
    }
    if ((dashStats?.overdueInvoices ?? 0) > 0) {
      items.push({
        msg: `${dashStats!.overdueInvoices} fatura(s) vencida(s) — risco de inadimplência`,
        severity: 'warning',
        path: '/faturas-cobrancas?status=vencido',
      });
    }
    if (aguardandoInstalacao > 0) {
      items.push({
        msg: `${aguardandoInstalacao} instalação(ões) pendente(s) — receita parada`,
        severity: 'warning',
        path: '/contracts?status=aguardando_instalacao',
      });
    }
    if (oltsOffline > 0) {
      items.push({
        msg: `${oltsOffline} OLT(s) offline — impacto na rede FTTH`,
        severity: 'error',
        path: '/ftth',
      });
    }
    if ((atendimentoData?.stats?.total_aguardando ?? 0) > 5) {
      items.push({
        msg: `${atendimentoData!.stats.total_aguardando} atendimentos WhatsApp aguardando resposta`,
        severity: 'info',
        path: '/atendimento/atendimentos',
      });
    }
    return items;
  }, [dashStats, aguardandoInstalacao, oltsOffline, atendimentoData]);

  const refreshAll = () => {
    refetchDash();
    refetchPerformance();
    refetchReg();
    refetchOverdue();
    refetchLogs();
  };

  const formatJsonData = (data: unknown) => {
    if (!data) return 'Nenhum dado';
    return JSON.stringify(data, null, 2);
  };

  const PIE_LABEL = ({ name, percent }: { name: string; percent: number }) =>
    `${name} ${(percent * 100).toFixed(0)}%`;

  return (
    <Box sx={{ ...dashboardPageSx, overflow: 'hidden', width: '100%', p: isMobile ? 1.5 : 2.5 }}>
      {/* Cabeçalho com refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title="Atualizar todos os dados">
          <IconButton onClick={refreshAll} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <HeroSection
        user={user}
        loading={loadingDash || loadingContracts}
        stats={
          dashStats
            ? {
                activeClients: dashStats.activeClients,
                monthlyRevenue: dashStats.monthlyRevenue,
                pendingTickets: dashStats.pendingTickets,
                crescimentoMensal: dashStats.crescimentoMensal ?? 0,
                contratosAtivos,
                oltsOnline,
                oltsTotal,
              }
            : null
        }
      />

      {/* Alertas críticos */}
      {alertasCriticos.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {alertasCriticos.map((alerta, i) => (
            <Alert
              key={i}
              severity={alerta.severity}
              icon={<WarningIcon fontSize="inherit" />}
              action={
                <Button color="inherit" size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(alerta.path)}>
                  Ver
                </Button>
              }
              sx={{ borderRadius: 2 }}
            >
              {alerta.msg}
            </Alert>
          ))}
        </Stack>
      )}

      {/* KPIs — Comercial & Financeiro */}
      <SecaoHeader
        titulo="Indicadores-chave do provedor"
        subtitulo="Base de assinantes, faturamento, inadimplência e operação de rede"
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Assinantes ativos"
            valor={fmtInt(dashStats?.activeClients)}
            subtitulo={`${fmtInt(dashStats?.totalClients)} cadastrados`}
            icone={<PeopleIcon />}
            cor={theme.palette.success.main}
            loading={loadingDash}
            onClick={() => navigate('/clients?status=ativo')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Contratos ativos"
            valor={fmtInt(contratosAtivos)}
            subtitulo={`${fmtInt(contratosBloqueados)} bloqueados`}
            icone={<DescriptionIcon />}
            cor={theme.palette.primary.main}
            loading={loadingContracts}
            onClick={() => navigate('/contracts?status=ativo')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="MRR recebido"
            valor={fmtCurrencyShort(monthlyRevenue)}
            subtitulo={new Date().toLocaleDateString('pt-BR', { month: 'long' })}
            icone={<AccountBalanceIcon />}
            cor={theme.palette.success.dark}
            loading={loadingDash}
            onClick={() => navigate('/faturas-cobrancas')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Ticket médio"
            valor={fmtCurrency(dashStats?.averageTicketPerInvoice)}
            subtitulo="Faturas pagas no mês"
            icone={<ReceiptLongIcon />}
            cor={theme.palette.secondary.main}
            loading={loadingDash}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Inadimplentes"
            valor={fmtInt(dashStats?.defaulters)}
            subtitulo={`${taxaInadimplencia.toFixed(1)}% da base ativa`}
            icone={<CreditCardOffIcon />}
            cor={theme.palette.error.main}
            loading={loadingDash}
            onClick={() => navigate('/faturas-cobrancas?status=vencido')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Faturas vencidas"
            valor={fmtInt(dashStats?.overdueInvoices)}
            subtitulo="Cobrança pendente"
            icone={<ReceiptIcon />}
            cor={theme.palette.warning.main}
            loading={loadingDash}
            onClick={() => navigate('/faturas-cobrancas?status=vencido')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Chamados abertos"
            valor={fmtInt(dashStats?.pendingTickets)}
            subtitulo={`${fmtInt(dashStats?.resolvedTickets)} resolvidos`}
            icone={<AssignmentIcon />}
            cor={theme.palette.info.main}
            loading={loadingDash}
            onClick={() => navigate('/chamados')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={1.5}>
          <KpiCard
            titulo="Ag. instalação"
            valor={fmtInt(aguardandoInstalacao)}
            subtitulo={`${fmtInt(aguardandoAssinatura)} ag. assinatura`}
            icone={<EngineeringIcon />}
            cor={theme.palette.warning.dark}
            loading={loadingContracts}
            onClick={() => navigate('/contracts?status=aguardando_instalacao')}
          />
        </Grid>
      </Grid>

      {/* KPIs — Financeiro detalhado + Rede */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <KpiCard
            titulo="Margem do mês"
            valor={`${margemLucro.toFixed(1)}%`}
            subtitulo={`Lucro: ${fmtCurrencyShort(lucroMes)}`}
            icone={<ShowChartIcon />}
            cor={margemLucro >= 0 ? theme.palette.success.main : theme.palette.error.main}
            loading={loadingDash || loadingExpenses}
            progress={Math.max(0, margemLucro)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            titulo="Despesas do mês"
            valor={fmtCurrencyShort(monthlyExpenses)}
            subtitulo={`${fmtInt(expenseStats?.overdue?.count)} vencidas`}
            icone={<ReceiptIcon />}
            cor={theme.palette.warning.main}
            loading={loadingExpenses}
            onClick={() => navigate('/contas-a-pagar')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            titulo="Churn mensal"
            valor={`${churnMes}%`}
            subtitulo={`${mesAtualReg?.cancelamentos ?? 0} cancelamentos · saldo ${mesAtualReg?.saldo ?? 0}`}
            icone={<TrendingDownIcon />}
            cor={Number(churnMes) > 2 ? theme.palette.error.main : theme.palette.success.main}
            loading={loadingReg}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            titulo="Rede FTTH"
            valor={oltsTotal ? `${oltsOnline}/${oltsTotal}` : '—'}
            subtitulo={oltsTotal ? `${fmtInt(onusAtivas)} ONUs · ${oltsOffline} OLT offline` : 'Sem OLTs cadastradas'}
            icone={<RouterIcon />}
            cor={oltsOffline > 0 ? theme.palette.error.main : theme.palette.info.main}
            loading={false}
            progress={oltsTotal ? (oltsOnline / oltsTotal) * 100 : undefined}
            onClick={() => navigate('/ftth')}
          />
        </Grid>
      </Grid>

      {/* Atendimento WhatsApp + Novos clientes */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2.5, p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <WhatsAppIcon color="success" />
              <Typography variant="subtitle2" fontWeight={700}>
                Atendimento WhatsApp
              </Typography>
              <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => navigate('/atendimento/dashboard')}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={1}>
              {[
                { label: 'Em atendimento', value: atendimentoData?.stats?.total_atendendo ?? 0, color: 'success.main' },
                { label: 'Aguardando', value: atendimentoData?.stats?.total_aguardando ?? 0, color: 'warning.main' },
                { label: 'Finalizados hoje', value: atendimentoData?.stats?.total_finalizados_hoje ?? 0, color: 'info.main' },
                { label: 'TMA (min)', value: Math.round(atendimentoData?.stats?.tma_minutos ?? 0), color: 'text.primary' },
              ].map((item) => (
                <Grid item xs={6} key={item.label}>
                  <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color={item.color}>
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {atendimentoData?.stats?.atendentes_online ?? 0} atendentes online agora
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2.5, p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonAddIcon color="success" />
              <Typography variant="subtitle2" fontWeight={700}>
                Novos clientes no mês
              </Typography>
              <Badge badgeContent={novosClientes?.length ?? 0} color="success" sx={{ ml: 'auto' }} />
            </Box>
            <List dense sx={{ maxHeight: 160, overflow: 'auto' }}>
              {(novosClientes ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum cadastro este mês
                </Typography>
              ) : (
                (novosClientes ?? []).slice(0, 5).map((c) => (
                  <ListItem key={c.id} disablePadding sx={{ py: 0.4 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ cursor: 'pointer', color: 'primary.main' }}
                          onClick={() => navigate(`/clients/${c.id}`)}
                        >
                          {c.nome}
                        </Typography>
                      }
                      secondary={`${c.cidade}/${c.estado} · ${c.data_cadastro}`}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2.5, p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HandymanIcon color="warning" />
              <Typography variant="subtitle2" fontWeight={700}>
                Pipeline operacional
              </Typography>
            </Box>
            <Stack spacing={1.25}>
              {[
                { label: 'Instalações pendentes', value: aguardandoInstalacao, path: '/contracts?status=aguardando_instalacao', icon: <EngineeringIcon fontSize="small" /> },
                { label: 'Contratos bloqueados', value: contratosBloqueados, path: '/contracts?status=bloqueado', icon: <BlockIcon fontSize="small" /> },
                { label: 'Suspensos', value: dashStats?.suspendedClients ?? 0, path: '/contracts', icon: <WarningIcon fontSize="small" /> },
                { label: 'Aniversariantes', value: aniversariantes?.length ?? 0, path: '', icon: <CakeIcon fontSize="small" />, onClick: () => setShowBirthdayModal(true) },
              ].map((item) => (
                <Box
                  key={item.label}
                  onClick={item.onClick ?? (() => item.path && navigate(item.path))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  {item.icon}
                  <Typography variant="body2" flex={1}>
                    {item.label}
                  </Typography>
                  <Chip label={item.value} size="small" color="primary" variant="outlined" />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos financeiros */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <ChartCard
            titulo="Performance financeira"
            subtitulo="Receita recebida, despesas, lucro, títulos emitidos e valor a receber — últimos 12 meses"
            onRefresh={refetchPerformance}
            loading={loadingPerformance}
            height={320}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={performanceChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="mes" tick={{ fill: chartColors.axis, fontSize: 11 }} />
                <YAxis
                  tick={{ fill: chartColors.axis, fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
                />
                <RechartsTooltip
                  formatter={(value: number) => fmtCurrency(value)}
                  contentStyle={rechartsStyles.tooltip.contentStyle}
                  labelStyle={rechartsStyles.tooltip.labelStyle}
                  itemStyle={rechartsStyles.tooltip.itemStyle}
                />
                <Legend wrapperStyle={{ color: rechartsStyles.legend.color, fontSize: 11 }} />
                <Area
                  type="monotone"
                  dataKey="lucro"
                  fill={alpha(theme.palette.success.main, 0.15)}
                  stroke={theme.palette.success.main}
                  name="Lucro"
                />
                <Line type="monotone" dataKey="revenue" stroke={rechartsStyles.series.revenue} strokeWidth={2} name="Receita" dot={false} />
                <Line type="monotone" dataKey="expenses" stroke={rechartsStyles.series.expenses} strokeWidth={2} name="Despesas" dot={false} />
                <Line type="monotone" dataKey="aReceber" stroke={rechartsStyles.series.aReceber} strokeWidth={2} name="A receber" dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="totalTitulos" stroke={rechartsStyles.series.totalTitulos} strokeWidth={2} name="Títulos emitidos" dot={false} strokeDasharray="2 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Gráficos comercial */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <ChartCard titulo="Status dos clientes" loading={loadingClientStatus} height={280}>
            {clientePieData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={clientePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={PIE_LABEL} labelLine={false}>
                    {clientePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v: number) => fmtInt(v)} contentStyle={rechartsStyles.tooltip.contentStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard titulo="Status dos contratos" loading={loadingContracts} height={280}>
            {contratoPieData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={contratoPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={PIE_LABEL} labelLine={false}>
                    {contratoPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v: number) => fmtInt(v)} contentStyle={rechartsStyles.tooltip.contentStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard
            titulo="Inadimplência mensal"
            subtitulo="Clientes distintos com faturas vencidas por mês de vencimento"
            onRefresh={refetchOverdue}
            loading={loadingOverdue}
            height={280}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overdueData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="mes" tick={{ fill: chartColors.axis, fontSize: 10 }} />
                <YAxis tick={{ fill: chartColors.axis, fontSize: 11 }} allowDecimals={false} />
                <RechartsTooltip contentStyle={rechartsStyles.tooltip.contentStyle} />
                <Bar dataKey="clientes" name="Clientes inadimplentes" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            titulo="Novos contratos vs cancelamentos"
            subtitulo="Saldo positivo = crescimento líquido da base"
            onRefresh={refetchReg}
            loading={loadingReg}
            height={300}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={regData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="mes" tick={{ fill: chartColors.axis, fontSize: 11 }} />
                <YAxis tick={{ fill: chartColors.axis, fontSize: 11 }} allowDecimals={false} />
                <RechartsTooltip contentStyle={rechartsStyles.tooltip.contentStyle} />
                <Legend wrapperStyle={{ color: rechartsStyles.legend.color, fontSize: 11 }} />
                <Bar dataKey="cadastros" name="Novos contratos" fill={rechartsStyles.series.registrations} radius={[3, 3, 0, 0]} />
                <Bar dataKey="cancelamentos" name="Cancelamentos" fill={rechartsStyles.series.cancellations} radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="saldo" name="Saldo líquido" stroke={theme.palette.success.main} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            titulo="Assinantes por plano"
            subtitulo="Serviços ativos vinculados a contratos não cancelados"
            loading={loadingPlanos}
            height={Math.max(300, (planosBarData.length || 1) * 32)}
          >
            {planosBarData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">Nenhum plano com assinantes</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={planosBarData} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} horizontal />
                  <XAxis type="number" tick={{ fontSize: 11, fill: chartColors.axis }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="nome"
                    width={130}
                    tick={{ fontSize: 10, fill: chartColors.axis }}
                    tickFormatter={(v: string) => (v.length > 22 ? `${v.slice(0, 22)}…` : v)}
                  />
                  <RechartsTooltip
                    formatter={(v: number) => [fmtInt(v), 'Assinantes']}
                    contentStyle={rechartsStyles.tooltip.contentStyle}
                  />
                  <Bar dataKey="quantidade" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Logs de auditoria */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard titulo="Auditoria — últimas ações no sistema" onRefresh={refetchLogs} loading={logsLoading} height={280}>
            {(logs ?? []).length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ pt: 8 }}>
                Nenhuma atividade recente
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 240 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ação</TableCell>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(logs ?? []).map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedLog(log);
                          setShowLogDetails(true);
                        }}
                      >
                        <TableCell>{logService.formatLogAction(log.acao)}</TableCell>
                        <TableCell>{log.usuario?.usuario ?? 'Sistema'}</TableCell>
                        <TableCell>{new Date(log.createdAt).toLocaleString('pt-BR')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* FAB — ações rápidas */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
      >
        <Fade in>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<WifiIcon />}
            onClick={() => navigate('/monitoramento/clientes')}
            sx={{ borderRadius: '20px', bgcolor: 'background.paper', display: { xs: 'none', sm: 'flex' } }}
          >
            Monitoramento
          </Button>
        </Fade>
        <Fade in>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/clients/new')}
            sx={{
              borderRadius: '25px',
              boxShadow: theme.shadows[8],
              px: 3,
              '&:hover': { transform: 'scale(1.03)' },
            }}
          >
            Novo Cliente
          </Button>
        </Fade>
      </Stack>

      {/* Dialog log */}
      <Dialog open={showLogDetails} onClose={() => setShowLogDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes do Log #{selectedLog?.id}</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Stack spacing={1}>
              <Typography><strong>Tipo:</strong> {logService.formatLogType(selectedLog.tipo)}</Typography>
              <Typography><strong>Ação:</strong> {logService.formatLogAction(selectedLog.acao)}</Typography>
              <Typography><strong>Descrição:</strong> {selectedLog.descricao}</Typography>
              <Typography><strong>Usuário:</strong> {selectedLog.usuario?.usuario ?? 'Sistema'}</Typography>
              <Typography><strong>Data:</strong> {format(new Date(selectedLog.createdAt), 'dd/MM/yyyy HH:mm')}</Typography>
              {selectedLog.metadata && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">Metadados</Typography>
                  <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    {formatJsonData(selectedLog.metadata)}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogDetails(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal aniversariantes */}
      <Dialog open={showBirthdayModal} onClose={() => setShowBirthdayModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CakeIcon color="secondary" />
            Aniversariantes do mês
            <Badge badgeContent={aniversariantes?.length ?? 0} color="secondary" sx={{ ml: 1 }} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <List dense>
            {(aniversariantes ?? []).length === 0 ? (
              <Typography color="text.secondary">Nenhum aniversariante este mês</Typography>
            ) : (
              (aniversariantes ?? []).map((c) => (
                <ListItem key={c.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.secondary.main, 0.15) }}>
                      <CakeIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => {
                          setShowBirthdayModal(false);
                          navigate(`/clients/${c.id}`);
                        }}
                      >
                        {c.nome}
                      </Typography>
                    }
                    secondary={c.data}
                  />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBirthdayModal(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
